import { useEffect, useRef, useState } from "react";
import { pickClip, spineConfig, spinePacks, type SpinePack } from "../../data/spineConfig";
import { assetExists } from "../../lib/assets";
import { loadGflSpineData, loadSpineRuntime, playClip, type PixiApp, type SpineSprite } from "../../lib/spine";
import styles from "./ChibiWalker.module.css";

type AnimMode = "combat" | "dorm";

type Props = {
  packId?: SpinePack["id"];
  packs?: SpinePack[];
  mode?: AnimMode;
  interactive?: boolean;
  height?: number;
  onReady?: (ready: boolean) => void;
};

type Bounds = { width: number; height: number };

function originY(h: number, clip: string) {
  const n = clip.toLowerCase();
  if (n.includes("pick") || n.includes("touch")) return h * 0.2;
  if (n.includes("sit") || n.includes("rest")) return h * 0.52;
  return h * 0.9;
}

function fitToStage(chibi: SpineSprite, w: number, h: number, clip = "") {
  chibi.scale.set(1);
  chibi.x = w / 2;
  chibi.y = originY(h, clip);
  const bounds =
    (chibi as unknown as { getLocalBounds?: () => Bounds }).getLocalBounds?.() ??
    (chibi as unknown as { getBounds?: () => Bounds }).getBounds?.();
  const bw = bounds?.width ?? 0;
  const bh = bounds?.height ?? 0;
  const usable = bw > 40 && bw < 900 && bh > 40 && bh < 900;
  const scale = usable
    ? Math.min((w * 0.42) / bw, (h * 0.78) / bh, 2.2)
    : h > 300
      ? 1.35
      : 0.72;
  chibi.scale.set(Math.max(0.7, scale));
  chibi.x = w / 2;
  chibi.y = originY(h, clip);
}

function clipsForMode(animations: string[], mode: AnimMode): string[] {
  const groups =
    mode === "dorm"
      ? [
          spineConfig.animations.idle,
          spineConfig.animations.move,
          spineConfig.animations.sit,
          spineConfig.animations.pick,
          spineConfig.animations.lie,
        ]
      : [
          spineConfig.animations.idle,
          spineConfig.animations.move,
          spineConfig.animations.attack,
          spineConfig.animations.victory,
        ];
  const names: string[] = [];
  for (const aliases of groups) {
    const name = pickClip(animations, aliases);
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

export function SpineStage({
  packId = "default",
  packs = spinePacks,
  mode = "combat",
  interactive = true,
  height = 380,
  onReady,
}: Props) {
  const host = useRef<HTMLDivElement>(null);
  const sprite = useRef<SpineSprite | null>(null);
  const size = useRef({ w: 640, h: height });
  const [failed, setFailed] = useState(false);
  const [clips, setClips] = useState<string[]>([]);
  const [clip, setClip] = useState("");

  useEffect(() => {
    const pack = packs.find((p) => p.id === packId) ?? packs[0];
    const el = host.current;
    if (!el || !pack) return;
    let destroyed = false;
    let app: PixiApp | null = null;
    let onCanvasClick: (() => void) | undefined;

    async function boot() {
      const has = await assetExists(pack.skel);
      if (!has) {
        setFailed(true);
        setClips([]);
        onReady?.(false);
        return;
      }
      try {
        const pixi = await loadSpineRuntime();
        if (destroyed || !el) return;
        const w = el.clientWidth || 640;
        const h = height;
        size.current = { w, h };
        app = new pixi.Application({
          width: w,
          height: h,
          transparent: true,
          antialias: true,
          backgroundAlpha: 0,
        });
        el.innerHTML = "";
        el.appendChild(app.view);
        const { spineData, animations } = await loadGflSpineData(pixi, pack);
        if (destroyed || !app) return;
        const chibi = new pixi.spine.Spine(spineData);
        sprite.current = chibi;
        const idle = pickClip(animations, spineConfig.animations.idle) ?? "wait";
        const cycle = clipsForMode(animations, mode);
        let i = 0;
        app.stage.addChild(chibi);
        (chibi as unknown as { update?: (dt: number) => void }).update?.(0);
        fitToStage(chibi, w, h, idle);
        playClip(chibi, idle, true);
        setClips(cycle.length ? cycle : animations);
        setClip(idle);
        if (interactive) {
          app.view.style.pointerEvents = "auto";
          app.view.style.cursor = "pointer";
          onCanvasClick = () => {
            if (!cycle.length) return;
            i = (i + 1) % cycle.length;
            const name = cycle[i];
            if (!name) return;
            playClip(chibi, name, true);
            chibi.y = originY(h, name);
            setClip(name);
          };
          app.view.addEventListener("click", onCanvasClick);
        }
        setFailed(false);
        onReady?.(true);
      } catch (err) {
        console.warn("Spine stage failed", err);
        sprite.current = null;
        setFailed(true);
        setClips([]);
        onReady?.(false);
      }
    }

    void boot();
    return () => {
      destroyed = true;
      sprite.current = null;
      if (onCanvasClick && app) app.view.removeEventListener("click", onCanvasClick);
      app?.destroy(true, true);
    };
  }, [packId, packs, mode, interactive, height, onReady]);

  const playNamed = (name: string) => {
    if (!sprite.current) return;
    playClip(sprite.current, name, true);
    sprite.current.y = originY(size.current.h, name);
    setClip(name);
  };

  return (
    <div>
      <div className={styles.stage} ref={host} style={{ height }}>
        {failed ? (
          <p className={styles.ph}>
            Spine 2.1 pack did not load. Check MondragonM1908.skel / .atlas / .png in
            public/assets/spine/mondragon/{packId}/.
          </p>
        ) : null}
      </div>
      {interactive && clips.length > 0 ? (
        <div className={styles.clips} role="toolbar" aria-label="Spine clips">
          {clips.map((name) => (
            <button
              key={name}
              type="button"
              className={styles.clip}
              aria-pressed={clip === name}
              onClick={() => playNamed(name)}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
